import axios, { AxiosError, AxiosInstance } from 'axios';

import { ApiError } from '@gtintel/signin/types/api.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8100/api';

// Create axios instance with defaults
export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true, // Important for cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth headers if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError<ApiError>) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const apiError: ApiError = {
        error: error.response.data?.error || 'An error occurred',
        detail: error.response.data?.detail,
        status_code: error.response.status,
      };

      // Handle specific status codes
      if (error.response.status === 401) {
        // Redirect to login if unauthorized
        const currentPath = window.location.pathname + window.location.search;
        if (!currentPath.includes('/login')) {
          window.location.href = `/login?redirect=${encodeURIComponent(window.location.href)}`;
        }
      }

      return Promise.reject(apiError);
    } else if (error.request) {
      // The request was made but no response was received
      const apiError: ApiError = {
        error: 'No response from server',
        detail: 'Please check your internet connection',
        status_code: 0,
      };
      return Promise.reject(apiError);
    }
    // Something happened in setting up the request that triggered an Error
    const apiError: ApiError = {
      error: 'Request failed',
      detail: error.message,
      status_code: 0,
    };
    return Promise.reject(apiError);
  }
);

export default api;
